// src/pages/PanoramicaSquadraPage.js
// "Panoramica squadra": scelta dell'asta (proprie o seguite) e della propria
// squadra; il cruscotto vero e proprio (previsioni, formazione, infortuni,
// prossima partita) è in components/panoramica/PanoramicaDashboard.js.
import React, { useEffect, useState } from 'react';
import './PanoramicaSquadraPage.css';
import '../pages/ScambiPage.css';
import TopBar from '../components/TopBar';
import { useAuth } from '../context/AuthContext';
import { getAuctionRolesMode, getUserAuctions, getUserWatchedAuctions } from '../firebase/auctionService';
import { getUserDisplayNamesByIds } from '../firebase/usersService';
import useAuctionRosterData from '../hooks/useAuctionRosterData';
import PanoramicaDashboard from '../components/panoramica/PanoramicaDashboard';
import { Loader2, RotateCcw, Eye } from 'lucide-react';

export default function PanoramicaSquadraPage() {
  const { user } = useAuth();
  const [auctions, setAuctions] = useState([]);
  const [watchedAuctions, setWatchedAuctions] = useState([]);
  const [watchedHostNames, setWatchedHostNames] = useState({});
  const [loadingAuctions, setLoadingAuctions] = useState(true);
  const [selection, setSelection] = useState(null); // { auctionId, ownerUid, isWatched }
  const [myTeamChoice, setMyTeamChoice] = useState('');

  useEffect(() => {
    if (!user?.uid) return;
    setLoadingAuctions(true);
    Promise.all([
      getUserAuctions(user.uid).catch(() => []),
      getUserWatchedAuctions(user.uid).catch(() => []),
    ])
      .then(async ([own, watched]) => {
        setAuctions(own);
        const ownIds = new Set(own.map((a) => String(a.id)));
        const followed = (watched || []).filter((a) => !ownIds.has(String(a.id)));
        setWatchedAuctions(followed);
        if (followed.length) {
          const names = await getUserDisplayNamesByIds(followed.map((a) => a.watchedOwnerUid)).catch(() => ({}));
          setWatchedHostNames(names);
        }
      })
      .finally(() => setLoadingAuctions(false));
  }, [user?.uid]);

  const rosterData = useAuctionRosterData(selection?.ownerUid, selection?.auctionId);

  const resetSelection = () => {
    setSelection(null);
    setMyTeamChoice('');
  };

  if (!selection) {
    return (
      <main className="container psq-page">
        <TopBar />
        <div className="scambi-auction-picker">
          <p className="scambi-hint">Scegli quale asta guardare: ti mostro la formazione consigliata per la prossima giornata.</p>

          {loadingAuctions ? (
            <div className="scambi-loading"><Loader2 size={20} className="spin" /> Carico le tue aste...</div>
          ) : auctions.length === 0 && watchedAuctions.length === 0 ? (
            <div className="trade-target-group__empty">
              Non hai ancora nessuna asta. Creane una dalla sezione "Aste", o segui un'asta pubblica.
            </div>
          ) : (
            <>
              {auctions.length > 0 && (
                <>
                  <h3 className="scambi-picker-group-title">Le tue aste</h3>
                  <div className="scambi-auction-grid">
                    {auctions.map((auction) => {
                      const auctionRolesMode = getAuctionRolesMode(auction);
                      return (
                        <button
                          key={auction.id}
                          type="button"
                          className="scambi-auction-pick"
                          onClick={() => setSelection({ auctionId: auction.id, ownerUid: user.uid, isWatched: false })}
                        >
                          <span className="scambi-auction-pick__name">{auction.name}</span>
                          <span className={`scambi-auction-pick__roles scambi-auction-pick__roles--${auctionRolesMode}`}>
                            {auctionRolesMode === 'mantra' ? 'Mantra' : 'Classic'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {watchedAuctions.length > 0 && (
                <>
                  <h3 className="scambi-picker-group-title"><Eye size={14} /> Aste che segui</h3>
                  <div className="scambi-auction-grid">
                    {watchedAuctions.map((auction) => {
                      const auctionRolesMode = getAuctionRolesMode(auction);
                      const hostName = watchedHostNames[auction.watchedOwnerUid];
                      return (
                        <button
                          key={auction.id}
                          type="button"
                          className="scambi-auction-pick"
                          onClick={() => setSelection({ auctionId: auction.id, ownerUid: auction.watchedOwnerUid, isWatched: true })}
                        >
                          <span className="scambi-auction-pick__main">
                            <span className="scambi-auction-pick__name">{auction.name}</span>
                            <span className="scambi-auction-pick__host">di {hostName || 'Utente'}</span>
                          </span>
                          <span className={`scambi-auction-pick__roles scambi-auction-pick__roles--${auctionRolesMode}`}>
                            {auctionRolesMode === 'mantra' ? 'Mantra' : 'Classic'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </main>
    );
  }

  if (rosterData.loading) {
    return (
      <main className="container psq-page">
        <TopBar />
        <div className="scambi-loading"><Loader2 size={20} className="spin" /> Carico l'asta selezionata...</div>
      </main>
    );
  }

  if (rosterData.notFound || !rosterData.auction) {
    return (
      <main className="container psq-page">
        <TopBar />
        <div className="trade-target-group__empty">
          Non sono riuscito a caricare questa asta.
          <button type="button" className="scambi-change-auction-btn" onClick={resetSelection}>Torna alla scelta dell'asta</button>
        </div>
      </main>
    );
  }

  const auctionTeamNames = rosterData.auction.teamNames || [];

  if (selection.isWatched && !myTeamChoice) {
    return (
      <main className="container psq-page">
        <TopBar />
        <div className="scambi-auction-picker">
          <div className="scambi-wtw__header">
            <div>
              <h2>{rosterData.auction.name}</h2>
              <span>{auctionTeamNames.length} squadre</span>
            </div>
            <button type="button" className="scambi-change-auction-btn" onClick={resetSelection}>
              <RotateCcw size={14} />
              Cambia asta
            </button>
          </div>
          <p className="scambi-hint">Quale squadra è la tua in questa asta?</p>
          <div className="scambi-auction-grid">
            {auctionTeamNames.map((team) => (
              <button key={team} type="button" className="scambi-auction-pick" onClick={() => setMyTeamChoice(team)}>
                <span className="scambi-auction-pick__name">{team}</span>
              </button>
            ))}
          </div>
        </div>
      </main>
    );
  }

  const myTeamName = selection.isWatched ? myTeamChoice : rosterData.myTeamName;
  const myRosterPurchases = selection.isWatched
    ? (rosterData.rosterByTeam?.[myTeamChoice] || [])
    : (rosterData.myRosterPurchases || []);

  return (
    <main className="container">
      <TopBar />
      <PanoramicaDashboard
        key={`${selection.auctionId}:${myTeamName}`}
        auction={rosterData.auction}
        ownerUid={selection.ownerUid}
        rolesMode={rosterData.rolesMode}
        myTeamName={myTeamName}
        myRosterPurchases={myRosterPurchases}
        players={rosterData.players}
        onChangeAuction={resetSelection}
        isWatched={selection.isWatched}
      />
    </main>
  );
}
